import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import { MultiBackend } from 'dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { Flex, Typo, theme, Loading, SimpleButton, ConfirmDelete } from '../../ui';
import { media } from '../../utils/media';
import { toRgba } from '../../ui/theme/colors';
import { drawerWidthMinimal, drawerWidthResponsive, drawerWidthResponsiveCompact } from '../../utils/constants';
import ThreeColumnLayout from '../../components/ThreeColumnLayout';
import { useGetCourseForBuilderQuery } from './editCourseApi';
import TocMenu from '../viewCourse/components/TocMenu';
import { setCourse } from '../course/hooks';
import { useMenuEditLeftMode, useSetMenuEditLeftMode, useSetMenuEditRightToggle, useLayoutOrganizerToggle, useSetLayoutOrganizerToggle, useEditRightPanelMode, useSetEditRightPanelMode } from '../global/hooks';
import { PANEL_MODES } from './editCourseState';
import { breakpoints } from '../../utils/media';
import {
  useInitializeCourseStructure,
  useSetCurrentPageId,
  useSetActiveSectionId,
  useGetPageByIdQuery,
  usePageTracking,
  useAutoPageSelection,
} from '../viewCourse/hooks';
import ModuleContentModal from './components/ModuleContentModal';
import VisualStructureEditor from './components/VisualStructureEditor';
import EditCourseRightPanel from '../../components/MenuSlider/EditCourseRightPanel';
import DraggableCoursePage from './components/DraggableCoursePage';
import QuizPageEditor from './components/QuizPageEditor';
import ModuleLayoutOrganizerModal from './components/ModuleLayoutOrganizerModal';
import { notifyError } from '../../services/notifications';
import { useEditCourseUIState, useModuleOperations } from './hooks';
import CourseGuide from '../courseGuide';
import { selectGuideIsActive } from '../courseGuide/courseGuideState';

const MainContentWrapper = styled(Flex)`
  height: 100%;
  position: relative;
`;

const CenteredMessage = styled.div`
  padding: ${({ $padding }) => $padding || '20px'};
  text-align: center;
`;

// Button wrapper for panel controls (desktop/tablet)
const ButtonPanel = styled.div`
  position: absolute;
  top: calc(${theme.headerSize} + 45px);
  left: ${({ $leftPanelCompact, $useCompactWidth }) => {
    if ($leftPanelCompact) return '15px';
    // Use compact width when both panels are open
    const width = $useCompactWidth ? drawerWidthResponsiveCompact : drawerWidthResponsive;
    return `calc(${width} - 25px)`;
  }};
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 20px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-shadow: 4px 0 8px ${toRgba(theme.colors.NEU_999, 0.05)};
  z-index: 90;
  transition: left 0.2s ease-in-out;

  ${media.lessThan('tablet')} {
    display: none;
  }
`;

const EditCourse = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { courseId, pageId: pageIdParam } = useParams();
  const guideIsActive = useSelector(selectGuideIsActive);

  // Convert pageId from string to number (or keep as 'diploma')
  const pageId = useMemo(() => {
    if (!pageIdParam) return undefined;
    if (pageIdParam === 'diploma') return pageIdParam;
    const n = Number(pageIdParam);
    return Number.isFinite(n) ? n : undefined;
  }, [pageIdParam]);

  const menuEditLeftMode = useMenuEditLeftMode();
  const setMenuEditLeftMode = useSetMenuEditLeftMode();
  const setMenuEditRightToggle = useSetMenuEditRightToggle();
  const layoutOrganizerToggle = useLayoutOrganizerToggle();
  const setLayoutOrganizerToggle = useSetLayoutOrganizerToggle();
  const setEditRightPanelMode = useSetEditRightPanelMode();

  const editRightPanelMode = useEditRightPanelMode();

  const { isSaving: isModuleSaving, saveModuleContent, deleteModule } = useModuleOperations({ pageId, t });

  const {
    // Panels
    isLeftPanelCompact,
    isRightPanelOpen,
    toggleLeftPanel,
    // toggleLeftPanelMode,
    toggleRightPanel,
    closeRightPanel,
    compactLeftPanel,
    openRightPanel,
    isMobileScreen,
    isMediumScreen,

    // Settings selection
    selectedModule,
    setSelectedModule,
    editModuleSettings,
    closeSettingsPanel,

    // Delete module dialog
    deleteModuleDialog,
    openDeleteModuleDialog,
    closeDeleteModuleDialog,
    confirmDeleteModule,

    // Content modal
    isContentModalOpen,
    pendingNewModule,
    openModuleContentModal,
    closeModuleContentModal,
    insertModule,
  } = useEditCourseUIState({
    breakpoints,
    menuEditLeftMode,
    setMenuEditLeftMode,
    setMenuEditRightToggle,
    PANEL_MODES,
  });

  // Layout organizer modal state
  const [isLayoutOrganizerOpen, setIsLayoutOrganizerOpen] = useState(false);

  // Open layout organizer when triggered from mobile header
  useEffect(() => {
    if (layoutOrganizerToggle) {
      setIsLayoutOrganizerOpen(true);
      setLayoutOrganizerToggle(false);
    }
  }, [layoutOrganizerToggle, setLayoutOrganizerToggle]);

  // ViewCourse action hooks (needed for TOC to work)
  const initializeCourseStructure = useInitializeCourseStructure();
  const setCurrentPageId = useSetCurrentPageId();
  const setActiveSectionId = useSetActiveSectionId();

  // Fetch course data using RTK Query
  const { data: courseData, isLoading, isError, error } = useGetCourseForBuilderQuery({ courseId: parseInt(courseId) }, { skip: !courseId });

  // Fetch full page data with modules (only when we have a pageId)
  const skipPageQuery = !pageId || pageId === 'diploma';
  const { data: pageData } = useGetPageByIdQuery(
    { pageId: pageId },
    {
      skip: skipPageQuery,
      refetchOnMountOrArgChange: true,
    },
  );

  // Redirect to dashboard if API returns 403 Forbidden
  useEffect(() => {
    if (error && error.status === 403) {
      // User is forbidden from accessing this course, redirect to dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [error, navigate]);

  // Redirect to view course if user doesn't have edit access
  useEffect(() => {
    if (courseData && courseData.canEdit === false) {
      // User doesn't have edit access, redirect to view course
      navigate(`/view/course/${courseId}${pageId ? `/${pageId}` : ''}`, { replace: true });
    }
  }, [courseData, courseId, pageId, navigate]);

  // Initialize course state for TOC to work
  useEffect(() => {
    if (courseData) {
      dispatch(setCourse(courseData));
      initializeCourseStructure(courseData);
    }
  }, [courseData, dispatch, initializeCourseStructure]);

  // Set current page ID when pageId changes
  useEffect(() => {
    if (pageId) {
      setCurrentPageId(pageId);
      // Close settings panel and clear selected module when page changes
      // (module settings are page-specific)
      setSelectedModule(null);
      closeRightPanel();
      setEditRightPanelMode('module');
    }
  }, [closeRightPanel, pageId, setCurrentPageId, setSelectedModule, setEditRightPanelMode]);

  // Find the current page data and section from the course TOC
  const { currentPageData, currentSection } = useMemo(() => {
    if (!courseData?.toc || !pageId) return { currentPageData: null, currentSection: null };

    for (const section of courseData.toc) {
      const page = section.pages?.find((p) => p.id === pageId);
      if (page) {
        return {
          currentPageData: { ...page, sectionId: section.id },
          currentSection: section,
        };
      }
    }
    return { currentPageData: null, currentSection: null };
  }, [courseData, pageId]);

  // Determine if current page is a quiz page
  const isQuizPage = useMemo(() => {
    const hasQuizEnabled = courseData?.settings?.quiz?.enabled;
    return hasQuizEnabled && currentSection?.type === 'quiz';
  }, [courseData, currentSection]);

  // Get quiz settings for current page
  const quizSettings = useMemo(() => {
    if (!isQuizPage || !pageId) return null;
    return courseData?.settings?.quiz?.pages?.[pageId] || {};
  }, [isQuizPage, pageId, courseData]);

  // Set active section when page data changes
  useEffect(() => {
    if (currentPageData?.sectionId) {
      setActiveSectionId(currentPageData.sectionId);
    }
  }, [currentPageData, setActiveSectionId]);

  // Use shared hook for automatic page selection
  useAutoPageSelection({
    isLoading,
    urlPageId: pageIdParam,
    courseData,
    navigate,
    courseId,
    setCurrentPageId,
    basePath: '/edit/course',
  });

  // Use shared hook for page tracking (marks pages as viewed for last-visited tracking)
  usePageTracking({
    pageId,
    courseId: courseId,
    pageData,
    isLocked: false, // Pages are never locked in edit mode
  });

  // Handle saving module content from modal
  const handleSaveModuleContent = useCallback(
    async (module, data) => {
      try {
        await saveModuleContent(module, data);
        // Close modal only on success
        closeModuleContentModal();
      } catch (error) {
        console.error('Failed to save module content:', error);
        notifyError(t('editCourse.saveFailed'));
      }
    },
    [closeModuleContentModal, saveModuleContent, t],
  );

  // Handle closing settings panel
  const handleCloseSettingsPanel = useCallback(() => {
    closeSettingsPanel();
    setEditRightPanelMode('module');
  }, [closeSettingsPanel, setEditRightPanelMode]);

  // Handle toggling quiz settings panel
  const handleOpenQuizSettings = useCallback(() => {
    if (editRightPanelMode === 'quiz') {
      handleCloseSettingsPanel();
      return;
    }

    setSelectedModule(null);
    setEditRightPanelMode('quiz');

    if (isMobileScreen()) {
      setMenuEditRightToggle(true);
    } else {
      if (isMediumScreen()) {
        compactLeftPanel();
      }
      openRightPanel();
    }
  }, [
    setSelectedModule,
    setMenuEditRightToggle,
    setEditRightPanelMode,
    editRightPanelMode,
    handleCloseSettingsPanel,
    isMobileScreen,
    isMediumScreen,
    compactLeftPanel,
    openRightPanel,
  ]);

  // Handle toggling course settings panel
  const handleOpenCourseSettings = useCallback(() => {
    if (editRightPanelMode === 'course') {
      handleCloseSettingsPanel();
      return;
    }

    setSelectedModule(null);
    setEditRightPanelMode('course');

    if (isMobileScreen()) {
      setMenuEditRightToggle(true);
    } else {
      if (isMediumScreen()) {
        compactLeftPanel();
      }
      openRightPanel();
    }
  }, [
    setSelectedModule,
    setMenuEditRightToggle,
    setEditRightPanelMode,
    editRightPanelMode,
    handleCloseSettingsPanel,
    isMobileScreen,
    isMediumScreen,
    compactLeftPanel,
    openRightPanel,
  ]);

  // Handle editing module settings (reset panel mode when switching to module settings)
  const handleEditModuleSettings = useCallback(
    (module) => {
      setEditRightPanelMode('module');
      editModuleSettings(module);
    },
    [editModuleSettings, setEditRightPanelMode],
  );

  // Handle editing module content (reset panel mode when opening content modal)
  const handleEditModuleContent = useCallback(
    (module) => {
      setEditRightPanelMode('module');
      openModuleContentModal(module);
    },
    [openModuleContentModal, setEditRightPanelMode],
  );

  // Handle inserting module (reset panel mode)
  const handleInsertModule = useCallback(
    (moduleType, position) => {
      setEditRightPanelMode('module');
      insertModule(moduleType, position);
    },
    [insertModule, setEditRightPanelMode],
  );

  const handleDeleteModule = useCallback(
    (module) => {
      openDeleteModuleDialog(module);
    },
    [openDeleteModuleDialog],
  );

  const handleConfirmDeleteModule = useCallback(async () => {
    await confirmDeleteModule(deleteModule);
  }, [confirmDeleteModule, deleteModule]);

  const leftPanelContent = useMemo(
    () =>
      menuEditLeftMode === PANEL_MODES.TOC ? (
        <TocMenu isExpanded={!isLeftPanelCompact} isStructureEditMode={true} />
      ) : (
        <VisualStructureEditor isExpanded={!isLeftPanelCompact} />
      ),
    [menuEditLeftMode, isLeftPanelCompact],
  );

  const rightPanelContent = useMemo(() => (
    <EditCourseRightPanel onClose={handleCloseSettingsPanel} />
  ), [handleCloseSettingsPanel]);

  // Main content area
  const mainContent = useMemo(
    () => (
      <MainContentWrapper direction='column'>
        {isLoading ? (
          <CenteredMessage>
            <Loading />
          </CenteredMessage>
        ) : isError ? (
          <CenteredMessage>
            <Typo.ContentSmallHeader>{t('course.error.notFound')}</Typo.ContentSmallHeader>
          </CenteredMessage>
        ) : !pageId ? (
          <CenteredMessage $padding={`${drawerWidthMinimal}px`}>
            <Typo.ContentSmallHeader>{t('course.selectPage')}</Typo.ContentSmallHeader>
            <Typo.Text>{t('course.selectPageDescription')}</Typo.Text>
          </CenteredMessage>
        ) : isQuizPage ? (
          <QuizPageEditor
            pageId={pageId}
            pageInfo={pageData}
            quizSettings={quizSettings}
            onEditModuleContent={handleEditModuleContent}
            onEditModuleSettings={handleEditModuleSettings}
            onDeleteModule={handleDeleteModule}
            onInsertModule={handleInsertModule}
            onOpenQuizSettings={handleOpenQuizSettings}
          />
        ) : (
          <DraggableCoursePage
            pageId={pageId}
            pageInfo={pageData}
            onEditModuleContent={openModuleContentModal}
            onEditModuleSettings={editModuleSettings}
            onDeleteModule={handleDeleteModule}
            onInsertModule={insertModule}
          />
        )}
      </MainContentWrapper>
    ),
    [
      isLoading,
      isError,
      pageId,
      pageData,
      openModuleContentModal,
      editModuleSettings,
      handleDeleteModule,
      insertModule,
      t,
      isQuizPage,
      quizSettings,
      handleEditModuleContent,
      handleEditModuleSettings,
      handleInsertModule,
      handleOpenQuizSettings,
    ],
  );

  // Calculate if we should use compact width (when both panels are open)
  const useCompactWidth = !isLeftPanelCompact && isRightPanelOpen;

  return (
    <>
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <ButtonPanel $leftPanelCompact={isLeftPanelCompact} $useCompactWidth={useCompactWidth}>
          {/* Toggle left panel compact/expanded */}
          <SimpleButton
            icon={isLeftPanelCompact ? 'DoubleArrowRight' : 'DoubleArrowLeft'}
            onClick={(e) => {
              e.stopPropagation();
              toggleLeftPanel();
            }}
            variant='ghost'
            size='small'
            noTooltip
            aria-label={isLeftPanelCompact ? t('coursePage.expand') : t('coursePage.minimize')}
          />
          <SimpleButton
            icon='Settings'
            onClick={(e) => {
              e.stopPropagation();
              handleOpenCourseSettings();
            }}
            variant='ghost'
            size='small'
            noTooltip
            aria-label={t('editCourse.courseSettingsAriaLabel')}
          />
          <SimpleButton
            icon='EditLayout'
            onClick={(e) => {
              e.stopPropagation();
              setIsLayoutOrganizerOpen(true);
            }}
            variant='ghost'
            size='small'
            noTooltip
            aria-label={t('editCourse.layoutOrganizer')}
          />
        </ButtonPanel>

        <ThreeColumnLayout
          leftPanelContent={leftPanelContent}
          mainContent={mainContent}
          rightPanelContent={rightPanelContent}
          isLeftPanelCompact={isLeftPanelCompact}
          isRightPanelOpen={isRightPanelOpen}
          onLeftPanelToggle={toggleLeftPanel}
          onRightPanelToggle={toggleRightPanel}
          exclusivePanelsOnMedium={true}
          context='edit'
        />

        {/* Module Content Editing Modal */}
        <ModuleContentModal
          open={isContentModalOpen}
          onClose={closeModuleContentModal}
          onSave={handleSaveModuleContent}
          module={pendingNewModule || selectedModule}
          isLoading={isModuleSaving}
        />

        {/* Delete Module Confirmation Modal */}
        <ConfirmDelete
          open={deleteModuleDialog.open}
          onClose={closeDeleteModuleDialog}
          onAcceptDelete={handleConfirmDeleteModule}
          title={t('editCourse.deleteModule')}
          description={t('editCourse.deleteModuleConfirm')}
        />

        {/* Module Layout Organizer Modal */}
        <ModuleLayoutOrganizerModal
          open={isLayoutOrganizerOpen}
          onClose={() => setIsLayoutOrganizerOpen(false)}
          pageRows={pageData?.pageRows || []}
          pageId={pageId}
        />
      </DndProvider>

      {guideIsActive && <CourseGuide courseId={parseInt(courseId)} />}
    </>
  );
};

export default EditCourse;
